import { gql, useQuery } from '@apollo/client';
import React, { useMemo, useRef, useState } from 'react';

import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { Config } from 'src/lib/config/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import {
  graphProposalListRouteConfig,
  variantProposalListRouteConfig,
} from '../../routes';

import { ProposalList } from './ProposalList';
import { ProposalNotificationToggle } from './ProposalNotificationToggle';
import { ProposeChangesButton } from './ProposeChangesButton';
import {
  SetProposalNotificationStatusModal,
  SetProposalNotificationStatusModalServiceFragment,
} from './SetProposalNotificationStatusModal';

export const ProposalListPageQuery = gql`
  query ProposalListPageQuery($graphId: ID!) {
    graph(id: $graphId) {
      id
      roles {
        canCreateProposal
      }
      variants {
        isContract
        id
        name
        subgraphs {
          name
        }
      }
      ...SetProposalNotificationStatusModalServiceFragment
    }
  }
  ${SetProposalNotificationStatusModalServiceFragment}
`;

export const ProposalListPage = () => {
  const { graphId } = useRouteParams(
    graphProposalListRouteConfig,
    variantProposalListRouteConfig,
  );
  const proposalDropdownButtonRefs = useRef<{
    [name: string]: React.RefObject<HTMLButtonElement>;
  }>({});
  const [proposalDropdownButton, setProposalDropdownButton] = useState<
    'header' | 'empty'
  >('header');

  const { data } = useQuery<
    GraphQLTypes.ProposalListPageQuery,
    GraphQLTypes.ProposalListPageQueryVariables
  >(ProposalListPageQuery, {
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    variables: {
      graphId,
    },
  });

  // TODO for the below we should maybe expose resolvers for the values bc they
  // are used by the filters & variants / subgraphs may not actually be in any proposals
  // when we determine the list from all variants / all subgraphs
  const allSubgraphsByVariant = useMemo(() => {
    return Object.fromEntries(
      data?.graph?.variants.map((variant): [string, string[]] => {
        return [
          variant.name,
          variant.subgraphs?.map((subgraph) => subgraph.name) ?? [],
        ];
      }) ?? [],
    );
  }, [data?.graph?.variants]);

  return (
    <>
      <PageTitle
        title={`Proposals for ${data?.graph?.name ?? graphId}`}
        rightSide={
          <>
            {data?.graph && (
              <>
                <ProposalNotificationToggle
                  notificationStatus={data.graph.proposalNotificationStatus}
                />
              </>
            )}
            <ProposeChangesButton
              // 🚨 Do not delete or change this data-testid / this button without updating the Proposals Datadog Browser test
              // TODO(Maya) add link to synthetics test
              data-testid="proposal_list_page_propose_changes_button"
              ref={(el: HTMLButtonElement | null) => {
                proposalDropdownButtonRefs.current.header = {
                  current: el,
                };
              }}
              disabled={!data?.graph?.roles?.canCreateProposal}
              tooltipPlacement="start"
              onClickMenuButton={() => setProposalDropdownButton('header')}
            />
          </>
        }
      />
      <ProposalList
        proposalDropdownButton={proposalDropdownButton}
        setProposalDropdownButton={setProposalDropdownButton}
        proposalDropdownButtonRefs={proposalDropdownButtonRefs}
        allSubgraphsByVariant={allSubgraphsByVariant}
      />

      <LazyUrlModal name={Config.modals.setProposalNotificationStatus}>
        {({ closeModal, isOpen }) =>
          data?.graph ? (
            <SetProposalNotificationStatusModal
              graph={data.graph}
              isOpen={isOpen}
              onClose={closeModal}
            />
          ) : null
        }
      </LazyUrlModal>
    </>
  );
};
